import { AnalogInput, AnalogTemperatureSensor, Author, BoilerAdapter, BoilerAdapterProtocol, Command, Comparator, ConditionalOperator, DayTimetable, Fob, FobButton, getAuthor, HeatingCircuit, IntervalTimetable, IOExtension, LogicExpression, Notification, NTCTemperatureCurve, OutputAction, Pause, SensorReading, TimeComparator, TimeInterval, User, WeekTimetable, WiredTemperatureSensor, } from 'z3k-config';
import { findAndChangeLinks, findLinksConfirmDelete, hasLinksTo, } from 'z3k-parser/lib/find-links';
import { changeSchedule2IntervalTimetable, intervalTimeTable2WeekSchedule, save2ConfigNewTimeInterval, weekSchedule2IntervalTimeTable, } from 'console/schedule-editor/transform-schedule';
import branding from 'shared/app/model/branding';
import { addAllPossibleCommands } from './commands';
import { partial } from 'webui/utils/functions';
import { getWiredTemperatureSensor } from 'z3k-parser/lib/sensors/wired';
import { HeatingMode } from 'z3k-parser/model/z3k-config/20-heating-mode';
import { isCircuitWithBoilerAdapter } from 'z3k-parser/lib/heating-circuit';
import { composePhysicalIO, idIn, objects2ids, parsePhysicalIO, seemsLikeObjectId, } from 'z3k-parser/lib';
import { renamingNTC } from 'console/climatic/utils/renamingNTC';
import { setupIOExtensions } from 'console/climatic/utils/setupIOExtensions';
import { isClimatic, isClimaticPro } from 'shared/contexts/device/config/utils';
import { BuiltInSerial } from 'z3k-parser/model/device-spec/built-in-serial';
import { hc_id_to_ai_id } from 'console/climatic/constants';
import { getDeviceSpecV2 } from 'z3k-parser/model/device-spec/z3k-feature-flags';
import { getIOExtensionSpecByBlockId } from 'z3k-parser/model/device-spec/io-extensions-spec';
export function checkConfigValid(checks) {
    const tmp = [checks];
    while (tmp.length > 0) {
        const el = tmp[0];
        tmp.splice(0, 1);
        if (typeof el === 'string')
            throw Error('CHECK FAILED');
        if (Array.isArray(el)) {
            tmp.push(...el);
        }
        else if (el !== null && typeof el === 'object') {
            for (const value of Object.values(el)) {
                tmp.push(value);
            }
        }
    }
}
export function preCleanConfig(config) {
    if (config.device?.name == 'SMART+') {
        const adapter_0000 = BoilerAdapter.get.one(config, (adapter) => adapter.serial.value == '0000');
        if (adapter_0000) {
            config = findAndChangeLinks(config, adapter_0000.id);
            config = BoilerAdapter.get.remove(config, (adapter) => adapter.id == adapter_0000.id);
        }
    }
    if (config.device?.name == 'H1V_PLUS') {
        // Removing OutputAction from test config used in production and
        // accidentally leaved in stock config
        const action = OutputAction.get.byId(config, 8339);
        if (action?.name == 'Тест выхода 1')
            config = OutputAction.get.removeById(config, action.id);
    }
    if (WiredTemperatureSensor.get.where(config, WiredTemperatureSensor.isInternetTemp)
        .length > 1) {
        const internet_sensors = WiredTemperatureSensor.get.where(config, WiredTemperatureSensor.isInternetTemp);
        const removes_sensor = internet_sensors.find((sensor) => !WiredTemperatureSensor.isOutside(sensor) &&
            !WiredTemperatureSensor.isOutsideReserve(sensor)) ?? internet_sensors[0];
        config = WiredTemperatureSensor.get.removeById(config, removes_sensor.id);
    }
    return config;
}
export function replaceFirmwareVersion(config, firmware_version) {
    return {
        ...config,
        device: {
            ...config.device,
            firmware: firmware_version.toString(),
        },
    };
}
export function replaceDeviceType(config, device_type) {
    return {
        ...config,
        device: {
            ...config.device,
            name: device_type,
        },
    };
}
export function addInternetTemp(config) {
    if (WiredTemperatureSensor.get.some(config, WiredTemperatureSensor.isInternetTemp))
        return config;
    return WiredTemperatureSensor.get.add(config, WiredTemperatureSensor.wts_internet_temp)[1];
}
function changeDayAndWeekToIntervalTimetable(config) {
    // Пока не сделаем свой редактор, не меняем для всех
    if (!branding.show_only_interval_timetable) {
        return config;
    }
    const new_config = changeSchedule2IntervalTimetable(config);
    const hms = HeatingMode.get.all(new_config);
    const tis = TimeInterval.get.all(new_config);
    return HeatingMode.get
        .where(new_config, ({ heating_zones }) => heating_zones.some(({ temperature_setting }) => seemsLikeObjectId(temperature_setting)))
        .reduce((new_config, { heating_zones }) => {
        for (const hz of heating_zones) {
            const it = IntervalTimetable.get.one(new_config, ({ id }) => hz.temperature_setting === id);
            if (it == null) {
                continue;
            }
            new_config = save2ConfigNewTimeInterval(new_config, weekSchedule2IntervalTimeTable(intervalTimeTable2WeekSchedule(it, tis, hms), it, tis));
        }
        return new_config;
    }, new_config);
}
export function renameThermostatRS485IoExtensions(config) {
    return IOExtension.get.modify(config, IOExtension.isML232Thermostat, (io_ext) => ({
        ...io_ext,
        name: 'Термостат RS485',
    }));
}
export function renameBuiltInIoExtensions(config) {
    return IOExtension.get.modify(config, IOExtension.isBuiltIn, (io_ext) => {
        let name = io_ext.name;
        if (io_ext.serial === BuiltInSerial.zero ||
            io_ext.serial === BuiltInSerial.first) {
            name = 'Сопроцессор';
        }
        return {
            ...io_ext,
            name,
        };
    });
}
export function prepareConfigForSettings(start_config, firmware_version, device_type) {
    return addAllPossibleCommands(changeDayAndWeekToIntervalTimetable(renameBuiltInIoExtensions(renameThermostatRS485IoExtensions(replaceDeviceType(replaceFirmwareVersion(addInternetTemp(preCleanConfig(start_config)), firmware_version), device_type)))));
}
/**
 * @function
 * @param config
 * @return Z3KConfig
 * Был заводской конфиг, в котором для внешних термостатов указаны входы для STM устройств (не PRO)
 */
function fixClimaticProAnalogInputs(config) {
    if (!isClimaticPro(config.device.name)) {
        return config;
    }
    //@ts-ignore
    console.log(Notification.get.byId(config, 20554));
    const device_spec = getDeviceSpecV2(config.device.name, config.device.hardware);
    if (device_spec == null) {
        return config;
    }
    const internal_io_ext = IOExtension.get.one(config, IOExtension.isBuiltIn);
    if (internal_io_ext == null) {
        return config;
    }
    const io_spec = getIOExtensionSpecByBlockId(device_spec, config, internal_io_ext.id);
    if (io_spec == null) {
        return config;
    }
    const ai_ids = [...hc_id_to_ai_id.values()];
    if (!AnalogInput.get.some(config, (ai) => {
        if (ai_ids.includes(ai.id)) {
            const { block_id } = parsePhysicalIO(ai.physical_input_num);
            return block_id == null;
        }
        return false;
    })) {
        return config;
    }
    return AnalogInput.get.modify(config, ({ id }) => ai_ids.includes(id), (ai) => {
        const index = ai.id == ai_ids[0] ? 0 : 1;
        return {
            ...ai,
            physical_input_num: composePhysicalIO(internal_io_ext.id, io_spec.inputs[index].value),
        };
    });
}
const missed_notification = {
    id: 20554,
    name: 'Связь с устройством восстановлена',
    recipient_list: [20533],
    setting_register: 17,
    sms_text: '$name$',
    voice_text: 'Связь_с_устройством_восстановлена',
};
/**
 * @function
 * @param config
 * @return Z3KConfig
 * Был заводской конфиг, в котором пропали уведомления для АЦШ
 */
function fixClimaticNotifications(config) {
    return Notification.get.createIfNotExists(config, missed_notification);
}
export function prepareClimaticConfigForSettings(start_config) {
    return fixClimaticNotifications(fixClimaticProAnalogInputs(addInternetTemp(renamingNTC(setupIOExtensions(start_config, [])))));
}
/**
 * @function
 * @param config
 * @return Z3KConfig
 * Буду чистить конфиг от неиспользуемых элементов
 */
function findAndRemoveUnusedObjectsAfterScenario(config) {
    let result_config = config;
    let do_while = true;
    do {
        let new_config = result_config;
        new_config = [
            Pause,
            ConditionalOperator,
            Comparator,
            LogicExpression,
            SensorReading,
            TimeComparator,
        ].reduce((current_config, object_type) => object_type.get.remove(current_config, (item) => findLinksConfirmDelete(current_config, item.id).length === 0), new_config);
        do_while = new_config !== result_config;
        result_config = new_config;
    } while (do_while);
    return result_config;
}
function ConnectPlusAirConsumerFilter(circuit) {
    return (HeatingCircuit.isConsumer(circuit) && HeatingCircuit.isAirConsumerMode(circuit));
}
/**
 * @function
 * @param config: Z3K-конфиг
 * @param device_spec: спецификация устройства
 * @return обновлённый конфиг
 * Изменяем конфиг для Baxi Connect+ в соответствии с тикетом #1186
 */
function changeHeatingCircuitForConnectPlus(config, device_spec) {
    //Отметаем всех без отопления
    if (!device_spec.heating || !device_spec.heating.has_heating) {
        return config;
    }
    //Отметаем не Connect+
    if (device_spec.heating.has_extended_heating.show_heat_request_with_air) {
        return config;
    }
    //Сначала находим все контуры потребителя c воздухом
    //1. Выставляем каждому из них «запрос на тепло» -> требуемая ТН
    //2. «максимальную температуру теплоносителя» взять из настроек котла
    //2.1 Находим контур котла в котором есть ОТ
    const max_temp = HeatingCircuit.get.one(config, partial(isCircuitWithBoilerAdapter, config))
        ?.water_max_temperature ?? null;
    const setter = //Если нашли, то ставим уставку из него
     max_temp == null
        ? {
            heat_request: HeatingCircuit.HEAT_REQUEST__CONSTANT,
        }
        : {
            heat_request: HeatingCircuit.HEAT_REQUEST__CONSTANT,
            water_max_temperature: max_temp,
        };
    return objects2ids(HeatingCircuit.get.where(config, ConnectPlusAirConsumerFilter)).reduce((updated_config, id) => HeatingCircuit.get.applySetterById(updated_config, id, setter), config);
}
function cleanTelephones(config) {
    return User.get.modify(config, () => true, (user) => ({
        ...user,
        phone: user.phone.replace(/[^0-9,+]/g, ''),
    }));
}
/**
 * @function
 * Если создали новые оповещения, то надо прописать в них пользователя
 * @param config: Z3K-конфиг
 * @return обновлённый конфиг
 * https://git.microline.ru/zont/server/-/merge_requests/2432
 */
function changeUserForNotifications(config) {
    const notifications = Notification.get.all(config);
    //Нечего менять
    if (notifications.length === 0) {
        return config;
    }
    const user = User.get.all(config)[0];
    //Нестандартная ситуация
    if (user == null) {
        return config;
    }
    //Добавляем единственного получателя
    return Notification.get.modify(config, ({ recipient_list }) => recipient_list.length === 0, (notification) => ({ ...notification, recipient_list: [user.id] }));
}
/**
 * @function
 * По ошибке в прошивке для адаптеров ARISTON стал выставляться другой тип
 * На данный момент тип влияет только на список логируемых сообщений
 * Решили изменить так, чтобы были все
 * @param config: Z3K-конфиг
 * @param device_spec: спецификация устройства
 * @return обновлённый конфиг
 * https://git.microline.ru/zont/server/-/issues/1318
 */
function changeBoilerLoggedParams(config, device_spec) {
    //Для pro-серии ничего не надо менять
    if (device_spec.io.builtin_io_extensions.length > 0) {
        return config;
    }
    const firmware = +config.device.firmware;
    //Неправильные прошивки
    if (firmware < 216 || firmware > 224) {
        return config;
    }
    return BoilerAdapter.get.modify(config, ({ type }) => type == BoilerAdapterProtocol.BSB, (ba) => ({ ...ba, logged_parameter_register: 127 }));
}
export function cleanConfig(config, device_spec, has_stationary_location, device) {
    IOExtension.get.all(config).forEach((item) => delete item.is_new);
    AnalogInput.get.all(config).forEach((item) => delete item.is_new);
    HeatingCircuit.get.all(config).forEach((item) => delete item.is_new);
    const referencedNTCs = AnalogTemperatureSensor.get
        .all(config)
        .map((sensor) => sensor.ntc);
    const fob_ids = Fob.get.all(config).map((fob) => fob.id);
    const fob_button_ids_to_delete = FobButton.get
        .except(config, (button) => fob_ids.includes(button.fob_id))
        .map((button) => button.id);
    const all_internet_weather_sensor = WiredTemperatureSensor.allInternetSensor(config);
    const should_delete_internet_temp = !has_stationary_location;
    const should_add_internet_temp = !should_delete_internet_temp &&
        WiredTemperatureSensor.get.dontExist(config, WiredTemperatureSensor.isInternetTemp);
    if (!isClimatic(device.device_type)) {
        // order is important:
        config = IntervalTimetable.get.remove(config, (tt) => !hasLinksTo(config, tt.id));
        config = TimeInterval.get.remove(config, (tt) => !hasLinksTo(config, tt.id));
        config = WeekTimetable.get.remove(config, (tt) => !hasLinksTo(config, tt.id));
        config = DayTimetable.get.remove(config, (tt) => !hasLinksTo(config, tt.id));
    }
    config = {
        ...config,
        ntc_temperature_curves: NTCTemperatureCurve.get.where(config, idIn(referencedNTCs)),
        fob_buttons: FobButton.get.except(config, idIn(fob_button_ids_to_delete)),
        commands: Command.get.where(config, (command) => hasLinksTo(config, command.id)),
        wired_temperature_sensors: (should_delete_internet_temp
            ? getWiredTemperatureSensor
            : WiredTemperatureSensor.get.all)(config),
        io_extensions: IOExtension.get.where(config, (ioExt) => getAuthor(ioExt.id) == Author.PREDEFINED ||
            IOExtension.shownToUser(ioExt) ||
            hasLinksTo(config, ioExt.id)),
    };
    config = changeHeatingCircuitForConnectPlus(findAndRemoveUnusedObjectsAfterScenario(config), device_spec);
    if (should_add_internet_temp) {
        config = addInternetTemp(config);
    }
    if (all_internet_weather_sensor.length > 1) {
        const link = all_internet_weather_sensor.filter((i) => !hasLinksTo(config, i.id))
            .length > 0
            ? all_internet_weather_sensor.filter((i) => !hasLinksTo(config, i.id))
            : all_internet_weather_sensor;
        config = WiredTemperatureSensor.get.remove(config, (sensor) => sensor.id === link[0].id);
    }
    config = cleanTelephones(config);
    //для упрощённой вкладки оповещения
    if (device_spec.show_simple_notification_tab) {
        config = changeUserForNotifications(config);
    }
    //исправляем багу с параметрами АЦШ
    config = changeBoilerLoggedParams(config, device_spec);
    return config;
}
